/**
 * Поиск по зданию в «Объёме 3D» (запрос владельца 28.07: «сделай максимально
 * удобно и на компьютере, и на телефоне»).
 *
 * Решение — один компонент в двух обличьях, как делают современные приложения:
 *  - на компьютере это командная палитра по центру (Ctrl/⌘+K, стрелки, Enter) —
 *    она не занимает место на экране, пока не нужна;
 *  - на телефоне — нижний лист (bottom sheet) с большим полем ввода у
 *    клавиатуры и списком под пальцем; лист перекрывает карту только когда
 *    открыт, а раньше список арендаторов торчал поверх сцены всегда.
 *
 * Ищет по ВСЕМУ зданию, а не только по открытому этажу, и показывает, на каком
 * этаже находится найденное: на 120 магазинах листать список бессмысленно.
 * Категории — быстрые фильтры-чипы: «Еда», «Одежда», «Туалеты» одним нажатием.
 */
import { tr } from '@app/i18n'
import { useEffect, useMemo, useRef, useState } from 'preact/hooks'
import { scoreKeyword } from '../search-text'
import type { VolumeModel } from '../volume3d/model'
import { categoryLabel } from './labels'

export interface FinderHit {
  readonly id: string
  readonly name: string
  readonly category: string
  readonly floor: number
}

/** Сколько результатов показываем: больше — уже не читается, а не помогает. */
const MAX_HITS = 40
/** Категории для чипов: те, что реально встречаются в этом здании. */
const MAX_CHIPS = 8

function allItems(model: VolumeModel | null): FinderHit[] {
  if (!model) return []
  const out: FinderHit[] = []
  for (const floor of model.floors) {
    for (const t of floor.tenants) {
      out.push({ id: t.id, name: t.name, category: t.category, floor: floor.index })
    }
  }
  return out
}

/**
 * Насколько объект подходит запросу. 0 — не подходит.
 * Порядок важен: начало названия → любое вхождение → категория → опечатка.
 * Нечёткое совпадение (опечатки) идёт последним и включается только если
 * точных нет — иначе «zara» находила пол-здания.
 */
function rank(item: FinderHit, query: string): number {
  const q = query.trim().toLowerCase()
  if (!q) return 1
  const name = item.name.toLowerCase()
  if (name.startsWith(q)) return 100
  if (name.includes(q)) return 80
  if (categoryLabel(item.category).toLowerCase().includes(q)) return 60
  return 0
}

/** Похожие названия — запасной проход, когда точных совпадений нет. */
function fuzzy(item: FinderHit, query: string): number {
  const q = query.trim().toLowerCase()
  if (q.length < 3) return 0
  // Порог подобран по живым данным: «kohvic» должен находить «Kohvik…»,
  // но случайные буквы не должны вытаскивать половину здания.
  const score = scoreKeyword(q, item.name.toLowerCase())
  return score >= 40 ? score / 2 : 0
}

/**
 * Отобрать и упорядочить результаты. Точные совпадения вытесняют похожие —
 * иначе на большом ТЦ любой запрос находит половину здания.
 */
export function rankHits(
  items: readonly FinderHit[],
  query: string,
  category: string | null,
): FinderHit[] {
  const pool = items.filter((item) => !category || item.category === category)
  const exact = pool.map((item) => ({ item, score: rank(item, query) })).filter((x) => x.score > 0)
  const scored =
    exact.length > 0
      ? exact
      : pool.map((item) => ({ item, score: fuzzy(item, query) })).filter((x) => x.score > 0)
  scored.sort(
    (a, b) =>
      b.score - a.score || a.item.floor - b.item.floor || a.item.name.localeCompare(b.item.name),
  )
  return scored.map((x) => x.item)
}

export function Volume3dFinder({
  model,
  open,
  onClose,
  onPick,
}: {
  model: VolumeModel | null
  open: boolean
  onClose: () => void
  /** Выбран объект: этаж + id — панель откроет интерьер и карточку. */
  onPick: (hit: FinderHit) => void
}) {
  const [query, setQuery] = useState('')
  const [category, setCategory] = useState<string | null>(null)
  const [cursor, setCursor] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)

  const items = useMemo(() => allItems(model), [model])

  // Чипы — самые частые категории здания: они и есть «фильтры одним нажатием».
  const chips = useMemo(() => {
    const count = new Map<string, number>()
    for (const item of items) count.set(item.category, (count.get(item.category) ?? 0) + 1)
    return [...count.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, MAX_CHIPS)
      .map(([cat]) => cat)
  }, [items])

  const found = useMemo(() => {
    const pool = items.filter((item) => !category || item.category === category)
    const exact = pool
      .map((item) => ({ item, score: rank(item, query) }))
      .filter((x) => x.score > 0)
    const scored =
      exact.length > 0
        ? exact
        : pool.map((item) => ({ item, score: fuzzy(item, query) })).filter((x) => x.score > 0)
    // Сначала точность, потом этаж и алфавит — список предсказуем.
    scored.sort(
      (a, b) =>
        b.score - a.score || a.item.floor - b.item.floor || a.item.name.localeCompare(b.item.name),
    )
    return scored.map((x) => x.item)
  }, [items, query, category])

  const hits = useMemo(() => found.slice(0, MAX_HITS), [found])
  const total = found.length

  useEffect(() => {
    if (open) {
      setCursor(0)
      // Фокус в поле — на телефоне сразу поднимается клавиатура.
      requestAnimationFrame(() => inputRef.current?.focus())
    }
  }, [open])

  if (!open) return null

  const move = (delta: number): void => {
    setCursor((prev) => Math.min(Math.max(prev + delta, 0), Math.max(hits.length - 1, 0)))
  }

  return (
    // Закрывает только клик по самому затемнению: сравнение target с
    // currentTarget избавляет от обработчика на листе (и от подавлений a11y).
    // С клавиатуры поиск закрывается по Escape в поле ввода.
    <div
      class="v3d-finder-backdrop"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div class="v3d-finder" role="dialog" aria-label={tr('volume3d.search')}>
        <div class="v3d-finder-head">
          <span class="v3d-finder-icon" aria-hidden="true">
            ⌕
          </span>
          <input
            ref={inputRef}
            class="v3d-finder-input"
            type="search"
            value={query}
            placeholder={tr('volume3d.search')}
            onInput={(e) => {
              setQuery((e.target as HTMLInputElement).value)
              setCursor(0)
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault()
                move(1)
              } else if (e.key === 'ArrowUp') {
                e.preventDefault()
                move(-1)
              } else if (e.key === 'Enter') {
                const hit = hits[cursor]
                if (hit) onPick(hit)
              } else if (e.key === 'Escape') {
                onClose()
              }
            }}
          />
          <button type="button" class="v3d-finder-close" onClick={onClose}>
            {tr('common.close')}
          </button>
        </div>

        <div class="v3d-finder-chips">
          <button
            type="button"
            class={`v3d-chip-mini${category === null ? ' v3d-chip-mini-on' : ''}`}
            onClick={() => setCategory(null)}
          >
            {tr('volume3d.all')}
          </button>
          {chips.map((cat) => (
            <button
              key={cat}
              type="button"
              class={`v3d-chip-mini${category === cat ? ' v3d-chip-mini-on' : ''}`}
              onClick={() => setCategory(category === cat ? null : cat)}
            >
              {categoryLabel(cat)}
            </button>
          ))}
        </div>

        <div class="v3d-finder-list">
          {hits.map((hit, i) => (
            <button
              key={hit.id}
              type="button"
              class={`v3d-finder-row${i === cursor ? ' v3d-finder-row-on' : ''}`}
              onMouseEnter={() => setCursor(i)}
              onClick={() => onPick(hit)}
            >
              <span class="v3d-finder-name">{hit.name}</span>
              <span class="v3d-finder-meta">
                {categoryLabel(hit.category)} · {tr('volume3d.floor_n', { n: String(hit.floor) })}
              </span>
            </button>
          ))}
          {hits.length === 0 && <p class="v3d-finder-empty">{tr('volume3d.search_empty')}</p>}
        </div>

        <p class="v3d-finder-foot">
          {total > hits.length
            ? tr('volume3d.search_more', { shown: String(hits.length), total: String(total) })
            : tr('volume3d.search_count', { n: String(total) })}
        </p>
      </div>
    </div>
  )
}
